import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography, Tooltip, useTheme } from "@mui/material";
import HelpOutline from "@mui/icons-material/HelpOutline";
import Close from "@mui/icons-material/Close";
import { LifeSuiteClient } from "../vendor/life-suite/life-suite-sdk";
import { ConversationPanel } from "../vendor/life-suite/ConversationPanel";
import { getLifeSuiteSession } from "../utils/lifeSuiteSession";

// The parent keys this component by authenticated account/session. Closing keeps
// the draft; signing out or changing accounts destroys all private mounted state.
export default function LifeSuiteSupport({ userId, sessionToken }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false), [started, setStarted] = useState(false);
  const [client, setClient] = useState(null), [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!started) return undefined;
    let active = true;
    setError("");
    getLifeSuiteSession(sessionToken).then(({ baseUrl }) => {
      if (!active) return;
      setClient(new LifeSuiteClient({ baseUrl, appTag: "life_circle", getSessionToken: async () => {
        const session = await getLifeSuiteSession(sessionToken);
        if (session.baseUrl !== baseUrl) throw new Error("Support connection changed. Reload the page to reconnect.");
        return session.token;
      } }));
    }).catch(e => { if (active) setError(e.message); });
    return () => { active = false; };
  }, [started, sessionToken, attempt]);
  const colors = { border: theme.palette.divider, surface: theme.palette.background.paper,
    textPrimary: theme.palette.text.primary, accent: theme.palette.primary.main,
    accentLight: theme.palette.action.hover, negative: theme.palette.error.main };
  return <>
    <Tooltip title="Help & support"><IconButton aria-label="Open Life-Circle support"
      onClick={() => { setStarted(true); setOpen(true); }} sx={{ width: 44, height: 44, color: "text.secondary" }}><HelpOutline /></IconButton></Tooltip>
    {started && <Dialog open={open} keepMounted onClose={() => setOpen(false)} aria-labelledby="life-circle-support-title" fullWidth maxWidth="sm"
      PaperProps={{ sx: { m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "100%" }, maxHeight: "calc(100dvh - 32px)", overflowWrap: "anywhere", fontFamily: theme.typography.fontFamily } }}>
      <DialogTitle id="life-circle-support-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        Life-Circle support<IconButton aria-label="Close support" sx={{ minWidth: 44, minHeight: 44 }} onClick={() => setOpen(false)}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 1, sm: 2 }, "& button, & select": { minHeight: 44 } }}>
        <Box sx={{ px: 1.75 }}><Typography variant="body2">App help from the Life-Suite team. Share only the details needed to explain the problem; private prayers and care records are not attached.</Typography></Box>
        {error ? <Alert severity="error" action={<Button onClick={() => setAttempt(n => n + 1)}>Retry</Button>}>{error}</Alert>
          : !client ? <Typography role="status" sx={{ p: 2 }}>Connecting to support…</Typography>
          : <ConversationPanel humanOnly client={client} T={colors} screen="life_circle_app" version="" storageKey={`life_circle_support_${userId}`} />}
      </DialogContent>
    </Dialog>}
  </>;
}
